import {
  Bot,
  CalendarDays,
  CheckCircle2,
  Circle,
  CircleAlert,
  CircleDot,
  Inbox,
  MessageSquare,
  OctagonAlert,
  UserRound,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ProjectTask, ProjectTaskAssignee } from '@/frontend/features/Fde/types'

export const taskStatuses: ProjectTask['status'][] = ['backlog', 'todo', 'in_progress', 'blocked', 'done']
export const taskStatusTone = {
  backlog: 'text-slate-500 bg-slate-500/10',
  todo: 'text-muted-foreground bg-muted',
  in_progress: 'text-blue-600 bg-blue-500/10',
  blocked: 'text-amber-700 bg-amber-500/10',
  done: 'text-emerald-600 bg-emerald-500/10',
}

export function TaskStatusIcon({
  status,
  className = 'size-4',
}: {
  status: ProjectTask['status']
  className?: string
}) {
  const Icon = { backlog: Inbox, todo: Circle, in_progress: CircleDot, blocked: CircleAlert, done: CheckCircle2 }[status]
  return <Icon className={className} />
}

export function TaskPeople({
  people,
  emptyLabel,
  compact = false,
  workStateLabels,
}: {
  people: ProjectTaskAssignee[]
  emptyLabel: string
  compact?: boolean
  workStateLabels?: Record<NonNullable<ProjectTaskAssignee['work_state']>, string>
}) {
  if (!people.length)
    return (
      <span
        title={emptyLabel}
        aria-label={emptyLabel}
        className={cn(
          'inline-flex items-center text-xs text-muted-foreground',
          compact ? 'size-6 justify-center rounded-full border border-dashed border-border' : 'gap-1.5',
        )}
      >
        <UserRound className="size-3.5" />
        {!compact && emptyLabel}
      </span>
    )
  return (
    <span
      title={people.map((person) => person.name).join(', ')}
      aria-label={people.map((person) => person.name).join(', ')}
      className="flex min-w-0 items-center gap-2"
    >
      <span className="flex shrink-0 -space-x-1.5">
        {people.slice(0, 2).map((person) => {
          const initials = person.name
            .trim()
            .split(/\s+/)
            .filter(Boolean)
            .map((word) => word[0])
            .slice(0, 2)
            .join('')
            .toLocaleUpperCase('pt-BR')
          return (
            <span
              key={person.value}
              title={[person.name, person.work_state && workStateLabels?.[person.work_state]]
                .filter(Boolean)
                .join(' · ')}
              className={cn(
                'flex size-6 items-center justify-center overflow-hidden rounded-full border-2 bg-muted text-muted-foreground',
                person.work_state === 'working' ? 'border-primary/60' : 'border-background',
              )}
            >
              {person.avatar_url ? (
                <img src={person.avatar_url} alt="" className="size-full object-cover" />
              ) : person.type === 'agent' ? (
                <Bot className="size-3" />
              ) : (
                <span className="text-[9px] font-semibold">{initials}</span>
              )}
            </span>
          )
        })}
      </span>
      {compact && people.length > 2 && (
        <span className="text-[10px] tabular-nums text-muted-foreground">+{people.length - 2}</span>
      )}
      {!compact && (
        <span className="truncate text-xs text-muted-foreground">
          {people[0].name}
          {people.length > 1 && ` +${people.length - 1}`}
        </span>
      )}
    </span>
  )
}

export function TaskPriority({
  priority,
  label,
  iconOnly = false,
}: {
  priority: ProjectTask['priority']
  label: string
  iconOnly?: boolean
}) {
  const bars = { low: 1, normal: 2, high: 3, urgent: 3 }[priority]
  return (
    <span
      title={label}
      aria-label={label}
      className={cn(
        'inline-flex shrink-0 items-center justify-center gap-1.5 text-[11px]',
        iconOnly ? 'size-5' : 'rounded-full border border-border/80 px-2 py-0.5',
        priority === 'urgent' ? 'text-destructive' : 'text-muted-foreground',
      )}
    >
      {priority === 'urgent' ? (
        <OctagonAlert className="size-3.5" />
      ) : (
        <svg viewBox="0 0 16 16" className="size-3.5" fill="currentColor" aria-hidden="true">
          {[0, 1, 2].map((bar) => (
            <rect
              key={bar}
              x={2 + bar * 4.5}
              y={9 - bar * 3}
              width="3"
              height={5 + bar * 3}
              rx="0.7"
              opacity={bar < bars ? 1 : 0.2}
            />
          ))}
        </svg>
      )}
      {!iconOnly && label}
    </span>
  )
}

export function TaskDueDate({
  value,
  overdue,
  label,
  className,
}: {
  value: string | null
  overdue: boolean
  label: string
  className?: string
}) {
  if (!value) return null
  return (
    <span
      title={`${label}: ${taskDate(value)}`}
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 text-[11px] tabular-nums',
        overdue ? 'text-destructive' : 'text-muted-foreground',
        className,
      )}
    >
      <CalendarDays className="size-3" aria-hidden="true" />
      <time dateTime={value}>{taskDate(value)}</time>
    </span>
  )
}

export function TaskCommentCount({ count, label }: { count: number; label: string }) {
  if (!count) return null
  return (
    <span
      title={`${label}: ${count}`}
      aria-label={`${label}: ${count}`}
      className="inline-flex shrink-0 items-center gap-1 text-[11px] tabular-nums text-muted-foreground"
    >
      <MessageSquare className="size-3" aria-hidden="true" />
      {count}
    </span>
  )
}

export function taskCreatedDate(value: string) {
  const date = new Date(value)
  return new Intl.DateTimeFormat('pt-BR', {
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' as const } : {}),
  }).format(date)
}

export function taskDate(value: string) {
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short' }).format(
    new Date(`${value}T12:00:00`),
  )
}
